if (!customElements.get("custom-navigator")) {
  class CustomNavigator extends HTMLElement {
    constructor() {
      super();
      this.navigators = [];
      this.activeByClick = false;
      this.ticking = false;
      this.scrollHandler = this.onScroll.bind(this);
    }

    connectedCallback() {
      this.floatingNav = this.closest(".floating-navigator");
      this.navigators = Array.from(this.querySelectorAll(".navigator-link"));

      this.initStickyObserver();

      this.navigators.forEach((navigator) => {
        navigator.addEventListener("click", (event) => {
          const sectionId = navigator.dataset.sectionId;
          const redirectLink = navigator.dataset.link;

          // PRIORITY 0 → Open On-Road Price modal
          if (navigator.dataset.openModal === "on-road-price") {
            const modal = document.getElementById("jawa-on-road-modal");

            if (modal) {
              event.preventDefault();
              this.activeByClick = true;
              this.setActive(navigator);

              document.dispatchEvent(new CustomEvent("jawa:open-on-road-modal"));

              setTimeout(() => {
                this.activeByClick = false;
              }, 1000);

              return;
            }

            // No modal on this page → fall through to redirect/href default.
          }

          // PRIORITY 1 → Section scroll
          if (sectionId) {
            event.preventDefault();

            const target = this.getTargetSection(sectionId);

            if (!target) return;

            this.activeByClick = true;
            this.setActive(navigator);

            target.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });

            setTimeout(() => {
              this.activeByClick = false;
            }, 1000);

            return;
          }

          // PRIORITY 2 → Redirect link
          if (redirectLink) {
            window.location.href = redirectLink;
          }
        });
      });

      window.addEventListener("scroll", this.scrollHandler, { passive: true });
      this.onScroll();
    }

    initStickyObserver() {
      if (!this.floatingNav) return;

      let sentinel = this.floatingNav.previousElementSibling;
      if (!sentinel || !sentinel.classList.contains("floating-navigator-sentinel")) {
        sentinel = document.createElement("div");
        sentinel.className = "floating-navigator-sentinel";
        sentinel.setAttribute("aria-hidden", "true");
        sentinel.style.cssText =
          "position: relative; width: 100%; height: 1px; margin-bottom: -1px; pointer-events: none; opacity: 0; visibility: hidden;";
        this.floatingNav.parentNode.insertBefore(sentinel, this.floatingNav);
      }
      this.sentinel = sentinel;
    }

    getTargetHeading() {
      // 1. Check heading in the first linked section
      const firstSectionId = this.navigators.find((n) => n.dataset.sectionId)?.dataset.sectionId;
      if (firstSectionId) {
        const section = this.getTargetSection(firstSectionId);
        if (section) {
          const heading = section.querySelector(".p360__heading, h1, h2, .p360__intro");
          if (heading) return heading;
        }
      }

      // 2. Check heading in subsequent sections (e.g. 360 view / hero)
      const nextSection = document.querySelector("product-360-view, .p360, [id*='360-view'], main h1");
      if (nextSection) {
        if (nextSection.matches("h1, h2, .p360__heading")) return nextSection;
        const heading = nextSection.querySelector(".p360__heading, h1, h2, .p360__intro");
        if (heading) return heading;
      }

      return null;
    }

    updateStickyState(isSticky) {
      if (!this.floatingNav) return;
      if (isSticky) {
        this.floatingNav.classList.add("is-sticky");
      } else {
        this.floatingNav.classList.remove("is-sticky");
      }
    }

    checkSticky() {
      if (!this.floatingNav) return;

      const heading = this.getTargetHeading();
      if (heading) {
        const navRect = this.floatingNav.getBoundingClientRect();
        const headingRect = heading.getBoundingClientRect();
        // Hide background when the top of the heading text reaches the floating navigator bar
        const navBottom = navRect.bottom || 60;
        const isSticky = headingRect.top <= navBottom;
        this.updateStickyState(isSticky);
      } else if (this.sentinel) {
        const rect = this.sentinel.getBoundingClientRect();
        const isSticky = rect.top <= -100;
        this.updateStickyState(isSticky);
      }
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this.scrollHandler);
      if (this.stickyObserver) {
        this.stickyObserver.disconnect();
      }
      if (this.sentinel && this.sentinel.parentNode) {
        this.sentinel.parentNode.removeChild(this.sentinel);
      }
    }

    getTargetSection(id) {
      if (!id) return null;

      // Direct ID match
      let target = document.getElementById(id);
      if (target) return target;

      // Shopify section wrapper fallback
      return document.querySelector(
        `div[id^="shopify-section"][id*="__${id}"], 
         div[id^="shopify-section"][id*="${id}"]`,
      );
    }

    setActive(activeNav) {
      this.navigators.forEach((nav) => nav.classList.remove("active"));

      if (!activeNav) return;

      activeNav.classList.add("active");

      // Mobile only: auto-scroll to center the active item
      const isMobile = window.matchMedia("(max-width: 749px)").matches;

      if (!isMobile) return;

      const container = activeNav.closest(".navigator-container");

      if (!container) return;

      requestAnimationFrame(() => {
        const containerWidth = container.offsetWidth;

        const scrollLeft =
          activeNav.offsetLeft - containerWidth / 2 + activeNav.offsetWidth / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      });
    }

    // Scroll logic
    onScroll() {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.checkSticky();
          this.updateActiveSection();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }

    updateActiveSection() {
      if (this.activeByClick) return;

      let closestIndex = -1;
      let minDistance = Infinity;

      this.navigators.forEach((nav, index) => {
        const id = nav.dataset.sectionId;

        // Ignore navigators without section IDs (redirects and the modal trigger)
        if (!id) return;

        const section = this.getTargetSection(id);

        if (!section) return;

        const rect = section.getBoundingClientRect();
        const distance = Math.abs(rect.top);

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== -1) {
        this.setActive(this.navigators[closestIndex]);
      }
    }
  }

  customElements.define("custom-navigator", CustomNavigator);
}
